import { useMe, useUserProducts } from "@/data/user";
import { useEffect, useState } from "react";
import PdfDiplomaPreview from "@/components/diploma-requests/PdfDiplomaPreview.tsx";
import diplomaBlurred from "@/assets/img/diploma-blurred.png";
import html2canvas from "html2canvas";
import { jsPDF } from "jspdf";
import { diplomaRequests } from "@/data/static/diploma.ts";
import dayjs from "dayjs";
import useGlobals from "@/utils/hooks/useGlobals.ts";
import { useNavigate } from "react-router-dom";
import useDiplomaRequest from "@/utils/hooks/useDiplomaRequest.ts";
import routes from "@/config/routes.ts";
import { ProductItem, SortedProducts } from "@/types";
import { Trans, useTranslation } from "react-i18next";
import LoadingButton from "@edusogno/primitives/loading-button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/Select.tsx";
import WarningIcon from "@/components/icons/book-exam/WarningIcon.tsx";
import Button from "@edusogno/primitives/button";
import PdfDiploma from "./PdfDiploma";

const MobileView = () => {
  const { requestedAlready, setState } = useDiplomaRequest();
  const { me } = useMe();
  const user = me?.item.user;
  const { setGlobalState } = useGlobals();
  const navigate = useNavigate();

  const { t } = useTranslation();

  const [isDiplomaUnlock, setIsDiplomaUnlock] = useState<boolean>(false);
  const [disabled, setDisabled] = useState(false);
  const [startDate, setStartDate] = useState("");
  const [courseName, setCourseName] = useState("");
  const [courseDuration, setCourseDuration] = useState(0);
  const [isDiplomaRequestUnlock, setIsDiplomaRequestUnlock] = useState(false);
  const [productOptions, setProductOptions] = useState<SortedProducts[]>([]);
  const [currentProductTypeof, setCurrentProductTypeof] = useState<
    string | null
  >(null);
  const [selectedProduct, setSelectedProduct] = useState<string>();
  const { products, isLoading } = useUserProducts();

  useEffect(() => {
    if (me) {
      if (
        me.item.user.crm_upselling &&
        me.item.user.crm_upselling.meeting_date
      ) {
        setState(
          "date",
          dayjs(me.item.user.crm_upselling?.meeting_date).format(
            "YYYY-MM-DD HH:mm:ss",
          ),
        );
        setState(
          "hour",
          dayjs(
            me.item.user.crm_upselling?.meeting_date,
            "YYYY-MM-DD HH:mm:ss",
          ).format("HH:mm"),
        );
        setState("showDownload", true);
        setState("requestedAlready", true);
      }
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [me]);

  useEffect(() => {
    if (products && products.items) {
      const groupedProducts = products.items.reduce(
        (acc: { [key: string]: ProductItem[] }, product) => {
          const productCourseName: string = product?.course_info?.course_name;
          if (!acc[productCourseName]) {
            acc[productCourseName] = [];
          }
          acc[productCourseName].push(product);
          return acc;
        },
        {},
      );

      const selectOptions = Object.values(groupedProducts).flatMap((group) => {
        const sortedGroup = group.sort((a: ProductItem, b: ProductItem) => {
          const startDateA = a.pivot?.start_date;
          const startDateB = b.pivot?.start_date;
          if (startDateA && startDateB) {
            return (
              new Date(startDateB).getTime() - new Date(startDateA).getTime()
            );
          } else {
            return 0;
          }
        });

        return sortedGroup.map((product: ProductItem, index) => ({
          label: product?.course_info?.course_name,
          value: index === 0 ? product.id : `${product.id}_1`,
          isNew: index === 0,
          startDate: product?.pivot?.start_date,
          ...product,
        }));
      }) as SortedProducts[];

      setProductOptions(selectOptions);
      const productSelected = selectOptions.find((option) => option.isNew);
      const selectedProductString = JSON.stringify(productSelected);
      setSelectedProduct(selectedProductString);
    }
  }, [products]);

  useEffect(() => {
    if (me && products && products.items) {
      // const upselling = Boolean(user?.crm_upselling);

      const currentProduct = selectedProduct
        ? (JSON.parse(selectedProduct) as ProductItem)
        : null;

      const excludePaths =
        !currentProduct?.product_tag
          ?.toLowerCase()
          .includes("lezioni individuali") ||
        !currentProduct?.product_tag?.toLowerCase().includes("ielts") ||
        false;

      // setIsDiplomaRequestUnlock(upselling || !excludePaths);

      let currentTypeof: string = "pot";

      if (currentProduct) {
        const startDateCourse = currentProduct?.pivot?.end_date;

        setStartDate(dayjs(startDateCourse).format("DD MMMM YYYY"));

        const endDateCourse = currentProduct?.pivot?.end_date;

        if (endDateCourse) {
          const subDays = dayjs(endDateCourse).subtract(21, "d");
          const now = dayjs();

          setIsDiplomaRequestUnlock(subDays <= now || !excludePaths);
        }

        const productInfo = currentProduct?.course_info
          ? currentProduct?.course_info
          : null;
        setCourseName(productInfo?.course_name || "");

        let sum = 0;
        let sumIelts = 0;

        const courseStructure = productInfo?.course_structure;

        courseStructure?.forEach((element) => {
          sumIelts += element.ielts;
          if (sumIelts > 0) {
            currentTypeof = "ielts";
          }

          sum +=
            element?.ielts +
            element?.grammar +
            element?.speaking +
            element?.one_to_one;
        });

        setCourseDuration(sum);
      }

      setCurrentProductTypeof(currentTypeof);
      setIsDiplomaUnlock(Boolean(currentProduct?.pivot?.diploma_unlocked));
    }
  }, [products, me, user, selectedProduct]);

  useEffect(() => {
    setGlobalState("backgroundClass", "bg-brand-light");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const exportPdf = async () => {
    setDisabled(true);

    const template = document.getElementById("captureDiplomaPDF");

    const canvas = await html2canvas(template!);

    const img = canvas.toDataURL("image/png");
    const width = 297;
    const height = width * (9 / 16);
    const doc = new jsPDF("l", "mm", [width, height]);

    doc.addImage(img, "PNG", 0, 0, width, height, "someAlias", "FAST");
    doc.save(user?.first_name?.split(" ")[0].toLowerCase() + "-diploma.pdf");
    setDisabled(false);
  };

  const handleShare = async () => {
    if (!navigator.share) {
      console.error("Web Share API not supported.");
      return;
    }
    const template = document.getElementById("captureDiplomaPDF");
    const canvas = await html2canvas(template!);
    const imgBlob = await new Promise((resolve) =>
      canvas.toBlob(resolve, "image/png"),
    );

    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-expect-error
    const file = new File([imgBlob], "diploma.png", { type: "image/png" });
    const shareData = {
      files: [file],
      title: "Diploma",
      text: "Guarda il mio diploma!",
    };

    try {
      await navigator.share(shareData);
    } catch (err) {
      console.error("Error sharing:", err);
    }
  };
  return (
    <div className={"mt-3 w-full gap-y-6 h-full flex flex-col"}>
      <div className={"flex flex-col items-center gap-4 px-3"}>
        {diplomaRequests.map((r, idx) => (
          <div
            key={"step" + idx}
            className={
              " bg-brand-lavender px-4 py-2 flex gap-x-3 items-center justify-between border-2 rounded-lg gap-2 border-solid border-brand-secondary  w-full"
            }
          >
            <div
              className={
                "flex items-center justify-center rounded bg-brand-secondary px-4 py-0.5"
              }
            >
              <p
                className={
                  " font[Inter] text-xs font-semibold  flex-shrink-0 text-light"
                }
              >
                {t("diploma_request.step")} {idx + 1}
              </p>
            </div>
            <p className={"text-brand text-xs text-start "}>
              <Trans
                i18nKey={r}
                components={{
                  b_anchor: <b className={"font-semibold"} />,
                }}
              />
            </p>
          </div>
        ))}
        <LoadingButton
          variant={"contained"}
          size={"md"}
          loading={isLoading}
          disabled={
            isDiplomaRequestUnlock ? isLoading : !isDiplomaRequestUnlock
          }
          onClick={() => {
            if (!requestedAlready) {
              navigate(routes.diploma_request_select_date);
            } else if (user?.crm_upselling?.meeting_date) {
              // if (isDiplomaUnlock) return;
              navigate(routes.diploma_request_request_notification);
            }
          }}
          className={"mb-4"}
        >
          {t("btns.request_diploma")}
        </LoadingButton>
      </div>
      <div className={"bg-brand-background h-full p-4 flex flex-col w-full"}>
        <div className={"flex flex-col items-center gap-y-4"}>
          <Select
            value={selectedProduct}
            onValueChange={(value) => {
              setSelectedProduct(value);
            }}
          >
            <SelectTrigger
              showIcon={false}
              className="w-full flex items-center bg-light text-sm border-0 text-brand"
            >
              <SelectValue placeholder="Select a product" />
            </SelectTrigger>
            <SelectContent>
              {productOptions.map((option) => (
                <SelectItem key={option.value} value={JSON.stringify(option)}>
                  <div className={"flex items-center gap-x-2 justify-between"}>
                    <span className={""}>{option.label}</span>
                    {option.isNew && (
                      <span className="text-white bg-brand-secondary float-right rounded text-xs px-2">
                        {t("diploma_request.new")}
                      </span>
                    )}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="w-full h-full max-h-full flex flex-col">
            <div className="w-full h-full overflow-hidden">
              {isDiplomaUnlock ? (
                <>
                  <PdfDiplomaPreview
                    english_course={courseName}
                    start_month={startDate}
                    english_level={user?.english_level || ""}
                    course_duration={courseDuration}
                    user={user}
                    type={currentProductTypeof}
                  />
                  <div className="opacity-0 pointer-events-none w-0 h-0">
                    <PdfDiploma
                      english_course={courseName}
                      start_month={startDate}
                      english_level={user?.english_level || ""}
                      course_duration={courseDuration}
                      user={user}
                      type={currentProductTypeof}
                    />
                  </div>
                </>
              ) : (
                <img
                  src={diplomaBlurred}
                  className="object-contain object-center h-full w-full block"
                  alt=""
                />
              )}
            </div>
          </div>
          <div
            className={
              "flex flex-row items-center gap-x-2 justify-center font-normal"
            }
          >
            <i className="shrink-0">
              <WarningIcon className={"fill-brand-secondary w-6 h-auto"} />
            </i>
            <p className="text-white text-xs">
              {t("diploma_request.share_goals")}
            </p>
          </div>
          <div
            className={"flex flex-row items-center gap-6 justify-center w-full"}
          >
            <Button
              size={"lg"}
              variant={"contained"}
              disabled={!isDiplomaUnlock || disabled || !selectedProduct}
              onClick={() => void exportPdf()}
            >
              {t("btns.download")}
            </Button>
            <LoadingButton
              disabled={!isDiplomaUnlock || disabled || !selectedProduct}
              loading={disabled}
              size={"lg"}
              variant={"contained"}
              color={"green"}
              onClick={() => void handleShare()}
            >
              {t("btns.share")}
            </LoadingButton>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileView;
